import { useState } from "react";
import { ChevronDown, List, Filter as FilterIcon, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { DealView } from "@/api/deals-api";
import {
  FilterNode,
  describeFilter,
  deserializeFilters,
  serializeFilters,
} from "./filters";

/**
 * The saved-view tab strip.
 *
 * One concept, two modes. A `filter` view is defined by its conditions; a `list`
 * view is defined by what you put in it. Both are workspace-shared, so the tabs
 * read the same for everyone on the fund — which is the point of having them.
 */
export function ViewTabs({
  views,
  activeViewId,
  onSelect,
  onSave,
  currentFilters,
  currentColumns,
  currentSort,
  isSaving,
}: {
  views: DealView[];
  activeViewId: string | null;
  onSelect: (viewId: string | null) => void;
  onSave: (views: Partial<DealView>[]) => Promise<boolean>;
  currentFilters: FilterNode[];
  currentColumns: string[] | null;
  currentSort: { field: string; direction: "asc" | "desc" };
  isSaving?: boolean;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [mode, setMode] = useState<"filter" | "list">("filter");

  const sorted = [...views].sort((a, b) => a.order - b.order);
  const activeView = sorted.find((v) => v._id === activeViewId) ?? null;

  /**
   * Whether the filters on screen have drifted from the ones the active view
   * was saved with.
   *
   * This is the whole answer to "which criteria belong to this view?" — the
   * chips in the filter bar are the view's criteria, editing them edits the
   * view, and this is what says so out loud. Compared through
   * `serializeFilters` so the saved shape (plain JSON off the workspace
   * document) and the live shape (FilterNode, possibly holding a half-built
   * row) normalise the same way before being compared.
   */
  const savedFilters = deserializeFilters(activeView?.filter ?? []);
  const hasUnsavedFilters =
    !!activeView &&
    activeView.mode !== "list" &&
    serializeFilters(currentFilters) !== serializeFilters(savedFilters);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    const saved = await onSave([
      ...sorted,
      {
        name: trimmed,
        order: sorted.length,
        mode,
        // A filter view captures what you are looking at now. A list starts
        // empty by definition — its contents are what you add to it.
        ...(mode === "filter"
          ? {
              filter: currentFilters,
              ...(currentColumns ? { columns: currentColumns } : {}),
              sort: currentSort,
            }
          : {}),
      },
    ]);

    // Keep the dialog open on failure — the parent has already explained why,
    // and closing would throw away what was typed.
    if (!saved) return;

    setName("");
    setMode("filter");
    setCreateOpen(false);
    toast.success(`View "${trimmed}" created`);
  };

  const remove = async (view: DealView) => {
    if (!(await onSave(sorted.filter((v) => v._id !== view._id)))) return;
    if (activeViewId === view._id) onSelect(null);
    toast.success(`View "${view.name}" deleted`);
  };

  /** Overwrite the active view with what is on screen. */
  const updateActive = async () => {
    if (!activeView) return;
    const saved = await onSave(
      sorted.map((view) =>
        view._id === activeView._id
          ? {
              ...view,
              filter: currentFilters,
              ...(currentColumns ? { columns: currentColumns } : {}),
              sort: currentSort,
            }
          : view
      )
    );
    if (!saved) return;
    toast.success(`"${activeView.name}" updated`);
  };

  return (
    <div className="flex items-center gap-1 overflow-x-auto border-b border-border px-6 py-1.5">
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={cn(
          "shrink-0 rounded-md px-2.5 py-1 text-xs transition-colors",
          activeViewId === null
            ? "bg-foreground/[0.06] font-medium text-foreground"
            : "text-muted-foreground hover:bg-foreground/[0.04]"
        )}
      >
        All
      </button>

      {sorted.map((view) => (
        <button
          key={view._id}
          type="button"
          onClick={() => onSelect(view._id)}
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors",
            activeViewId === view._id
              ? "bg-foreground/[0.06] font-medium text-foreground"
              : "text-muted-foreground hover:bg-foreground/[0.04]"
          )}
          title={
            view.mode === "list"
              ? "A list — deals you added"
              : (view.filter ?? [])
                  .map((f) => describeFilter(f as FilterNode))
                  .join(" · ") || "No filters"
          }
        >
          {view.mode === "list" ? (
            <List className="h-3 w-3" />
          ) : (
            <FilterIcon className="h-3 w-3" />
          )}
          {view.name}
        </button>
      ))}

      {/* A filter view *is* its filters, so editing the filter bar edits the
          view. Saying so here — with both ways out one click away — is what
          stops "saved a view, no idea what's in it" being a guess. */}
      {hasUnsavedFilters && (
        <div className="ml-1 inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-foreground/[0.03] py-0.5 pl-2 pr-0.5 text-xs">
          <span className="text-muted-foreground">Filters changed</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-xs"
            disabled={isSaving}
            onClick={updateActive}
          >
            Save to view
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-xs text-muted-foreground"
            disabled={isSaving}
            onClick={() => onSelect(activeView._id)}
          >
            Discard
          </Button>
        </div>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 gap-1 px-2 text-xs text-muted-foreground"
            disabled={isSaving}
          >
            Manage views
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuItem onSelect={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-3.5 w-3.5" />
            New view
          </DropdownMenuItem>

          {activeView && (
            <DropdownMenuItem onSelect={updateActive}>
              Save current setup to &ldquo;{activeView.name}&rdquo;
            </DropdownMenuItem>
          )}

          {sorted.length > 0 && <DropdownMenuSeparator />}

          {sorted.map((view) => (
            <DropdownMenuItem
              key={view._id}
              className="text-destructive focus:text-destructive"
              onSelect={() => remove(view)}
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete &ldquo;{view.name}&rdquo;
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New view</DialogTitle>
            <DialogDescription>
              Everyone in the workspace sees the same views.
            </DialogDescription>
          </DialogHeader>

          <Input
            autoFocus
            placeholder="Name, e.g. Pipeline or EVC"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()}
          />

          <div className="space-y-2">
            {(
              [
                {
                  value: "filter" as const,
                  title: "From the current filters",
                  detail:
                    "Deals matching your filters right now. Membership updates itself as deals change.",
                },
                {
                  value: "list" as const,
                  title: "A list I add to",
                  detail:
                    "Starts empty. Add deals to it from the table; nothing joins on its own.",
                },
              ]
            ).map((option) => (
              <label
                key={option.value}
                className={cn(
                  "flex cursor-pointer gap-2.5 rounded-md border p-2.5",
                  mode === option.value
                    ? "border-foreground/30 bg-foreground/[0.03]"
                    : "border-border"
                )}
              >
                <input
                  type="radio"
                  className="mt-1"
                  checked={mode === option.value}
                  onChange={() => setMode(option.value)}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{option.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {option.detail}
                  </span>

                  {/* Spell out exactly what is about to be captured. Saving a
                      view used to snapshot the filter bar silently, which left
                      no way to tell a view that holds three conditions from one
                      that holds none. */}
                  {option.value === "filter" &&
                    (currentFilters.length > 0 ? (
                      <span className="mt-2 flex flex-wrap gap-1">
                        {currentFilters.map((node, index) => (
                          <span
                            key={index}
                            className="inline-flex max-w-full items-center truncate rounded border border-border bg-background px-1.5 py-0.5 text-[11px] text-foreground"
                          >
                            {describeFilter(node)}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="mt-2 block text-xs text-amber-700">
                        No filters are applied right now, so this view would
                        show every deal. Close this, set the filters you want,
                        then create the view.
                      </span>
                    ))}
                </span>
              </label>
            ))}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={create} disabled={!name.trim() || isSaving}>
              Create
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
